import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, CATEGORIES } from "@/lib/pfc";
import TalentCard from "@/components/TalentCard";
import { Search, Trophy } from "lucide-react";

export default function Directory() {
  const [params, setParams] = useSearchParams();
  const initialCat = params.get("category") || "";
  const [category, setCategory] = useState(initialCat);
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flaujae, setFlaujae] = useState(null);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (category) q.set("category", category);
    if (search) q.set("search", search);
    api.get(`/profiles?${q.toString()}`).then((r) => { setItems(r.data); setLoading(false); });
  }, [category, search]);

  useEffect(() => {
    api.get("/profiles?search=flaujae").then((r) => {
      if (r.data && r.data.length > 0) setFlaujae(r.data[0]);
    }).catch(() => {});
  }, []);

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14" data-testid="directory-page">
      <div className="mb-10">
        <div className="label text-pfc-gold mb-3">Public Figure Directory</div>
        <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl text-white uppercase leading-[0.9]">
          The <span className="gold-text">Talent</span> Index
        </h1>
        <p className="text-white/60 max-w-2xl mt-4 font-body text-sm">
          Searchable profiles of artists, models, athletes, business owners, and creators on the rise.
        </p>
      </div>

      {/* SPORTS SPOTLIGHT — Seattle Storm + Flaujae Johnson */}
      {flaujae && (!category || category === "athletes") && (
        <Link
          to={`/profile/${flaujae.profile_id}`}
          className="block mb-10 group"
          data-testid="sports-spotlight-flaujae"
        >
          <div className="relative overflow-hidden border border-pfc-gold/40 bg-gradient-to-r from-ink-900 via-ink-800 to-ink-900 grain hover:border-pfc-gold/70 transition">
            <div className="grid md:grid-cols-[1fr_2fr] gap-0">
              <div className="relative h-56 md:h-auto overflow-hidden">
                <img
                  src={flaujae.cover_image}
                  alt="Flaujae Johnson · Seattle Storm"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-ink-900/60" />
              </div>
              <div className="p-7 lg:p-9 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-3">
                  <Trophy className="text-pfc-gold" size={16} />
                  <span className="label text-pfc-gold">Sports Spotlight · Team of the Month</span>
                </div>
                <h2 className="font-display text-3xl sm:text-4xl text-white uppercase leading-[0.95]">
                  Seattle <span className="gold-text">Storm</span> · WNBA
                </h2>
                <p className="font-display text-2xl text-white/90 mt-2">
                  Featuring <span className="silver-text">Flaujae Johnson</span>
                </p>
                <p className="font-body text-sm text-white/60 mt-3 line-clamp-2 max-w-xl">
                  Two-sport phenom turned franchise spark — Flaujae brings championship pedigree, downhill scoring,
                  and culture-shifting charisma to one of the WNBA's most decorated teams.
                </p>
                <span className="mt-5 font-sub uppercase tracking-[0.25em] text-xs text-pfc-gold group-hover:text-pfc-gold-bright transition self-start">
                  View Profile →
                </span>
              </div>
            </div>
          </div>
        </Link>
      )}


      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={16} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, tag, or headline…"
            className="w-full bg-ink-700 border border-white/10 text-white pl-11 pr-4 py-3 font-body focus:border-pfc-gold focus:outline-none focus:ring-1 focus:ring-pfc-gold"
            data-testid="directory-search-input"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => { setCategory(""); setParams({}); }}
            className={`font-sub uppercase tracking-[0.2em] text-xs px-4 py-3 border transition ${
              !category ? "bg-pfc-gold border-pfc-gold text-ink-900" : "border-white/15 text-white/70 hover:border-white/40"
            }`}
            data-testid="filter-all"
          >All</button>
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => { setCategory(c.key); setParams({ category: c.key }); }}
              className={`font-sub uppercase tracking-[0.2em] text-xs px-4 py-3 border transition ${
                category === c.key ? "bg-pfc-gold border-pfc-gold text-ink-900" : "border-white/15 text-white/70 hover:border-white/40"
              }`}
              data-testid={`filter-${c.key}`}
            >{c.label.split(" ")[0]}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-white/40 text-center py-20 font-sub uppercase tracking-[0.2em]">Loading talent…</div>
      ) : items.length === 0 ? (
        <div className="text-white/40 text-center py-20 font-sub uppercase tracking-[0.2em]" data-testid="directory-empty">No talent found · adjust your search</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="directory-grid">
          {items.map((p) => <TalentCard key={p.profile_id} profile={p} />)}
        </div>
      )}
    </div>
  );
}
